import React, { useState } from 'react';
import { QrCode, Printer, Download, MapPin, CheckCircle, ShieldCheck, X, Building, Eye } from 'lucide-react';
import { PatrolCheckpoint } from '../types';

interface Props {
  checkpoints: PatrolCheckpoint[];
  isOpen: boolean;
  onClose: () => void;
}

// Generate an SVG QR code visual pattern based on seed text
const QRMatrix: React.FC<{ value: string; size?: number }> = ({ value, size = 140 }) => {
  // Simple deterministic pseudo-random hash generator from string to build a crisp, realistic QR grid
  const hash = value.split('').reduce((acc, char, i) => acc + char.charCodeAt(0) * (i + 1), 42);
  const gridSize = 19; // 19x19 standard QR grid
  const cells: boolean[][] = [];

  for (let r = 0; r < gridSize; r++) {
    cells[r] = [];
    for (let c = 0; c < gridSize; c++) {
      // Corners 7x7 position finder patterns
      const isTopLeft = r < 7 && c < 7;
      const isTopRight = r < 7 && c >= gridSize - 7;
      const isBottomLeft = r >= gridSize - 7 && c < 7;

      if (isTopLeft || isTopRight || isBottomLeft) {
        const localR = isTopLeft ? r : isTopRight ? r : r - (gridSize - 7);
        const localC = isTopLeft ? c : isTopRight ? c - (gridSize - 7) : c;
        // 7x7 outer square & 3x3 inner square
        if (localR === 0 || localR === 6 || localC === 0 || localC === 6) {
          cells[r][c] = true;
        } else if (localR >= 2 && localR <= 4 && localC >= 2 && localC <= 4) {
          cells[r][c] = true;
        } else {
          cells[r][c] = false;
        }
      } else {
        // Pseudo random data modules
        const bit = ((hash * (r + 1) * 31 + (c + 1) * 17 + r * c) % 100) > 48;
        // Timing patterns
        if (r === 6 || c === 6) {
          cells[r][c] = (r + c) % 2 === 0;
        } else {
          cells[r][c] = bit;
        }
      }
    }
  }

  const cellSize = size / gridSize;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="rounded-lg bg-white p-1 shadow-xs">
      {cells.map((row, r) =>
        row.map((filled, c) =>
          filled ? (
            <rect
              key={`${r}-${c}`}
              x={c * cellSize}
              y={r * cellSize}
              width={cellSize + 0.2}
              height={cellSize + 0.2}
              fill="#0f172a"
              rx={cellSize * 0.15}
            />
          ) : null
        )
      )}
    </svg>
  );
};

export const CheckpointQRCardsModal: React.FC<Props> = ({ checkpoints, isOpen, onClose }) => {
  const [selectedCheckpoint, setSelectedCheckpoint] = useState<PatrolCheckpoint | null>(null);

  if (!isOpen) return null;

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto animate-fadeIn">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden my-auto">
        {/* Header */}
        <div className="px-6 py-4 bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-950 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              <QrCode className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-base sm:text-lg flex items-center gap-2">
                แผ่นป้าย QR Code จุดตรวจความปลอดภัย (Security Checkpoint Signs)
              </h3>
              <p className="text-xs text-slate-300">
                พิมพ์และนำไปติดตั้งตามจุดตรวจทั้ง 7 โซนทั่วโครงการ นราวดี อิลิแกนซ์
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrint}
              className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-xs font-semibold flex items-center gap-1.5 shadow-sm transition-colors"
            >
              <Printer className="w-3.5 h-3.5" />
              พิมพ์ป้ายทั้งหมด
            </button>
            <button
              onClick={onClose}
              className="p-2 text-slate-300 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content list */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1 bg-slate-50">
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-xs text-emerald-800 flex items-start gap-3">
            <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-emerald-900">คำแนะนำการติดตั้งป้าย QR Code Checkpoint:</p>
              <p className="text-emerald-700 mt-0.5 leading-relaxed">
                1. พิมพ์ป้ายแล้วเคลือบพลาสติกกันน้ำ หรือใส่กรอบอะคริลิกกันฝน<br />
                2. ติดตั้งในระดับสายตา (สูงจากพื้น 140 - 160 ซม.) ในจุดที่มองเห็นได้ชัดเจน<br />
                3. รปภ. สามารถใช้กล้องมือถือในระบบสแกนเพื่อบันทึกเวลาและการตรวจเช็คความปลอดภัยได้ทันที
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {checkpoints.map((cp) => (
              <div
                key={cp.id}
                className="bg-white rounded-2xl p-5 border-2 border-slate-200 hover:border-emerald-500 transition-all shadow-xs flex flex-col justify-between relative group"
              >
                {/* Header Tag */}
                <div className="flex items-start justify-between gap-2 border-b border-slate-100 pb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-0.5 bg-emerald-700 text-white text-[11px] font-mono font-bold rounded-md">
                        {cp.code}
                      </span>
                      <span className="text-[11px] font-semibold text-slate-500">
                        จุดที่ {cp.sequence} • {cp.zone}
                      </span>
                    </div>
                    <h4 className="font-bold text-slate-900 text-sm mt-1.5 leading-snug">
                      {cp.name}
                    </h4>
                  </div>
                </div>

                {/* Body with QR Matrix */}
                <div className="py-4 flex items-center justify-between gap-4">
                  <div className="space-y-1.5 flex-1">
                    <p className="text-[11px] text-slate-500 line-clamp-2">{cp.description}</p>
                    <div className="bg-slate-100 px-2.5 py-1 rounded-lg text-[10px] font-mono text-slate-600 break-all select-all">
                      QR Payload: <span className="font-bold text-emerald-800">{cp.qr_data}</span>
                    </div>
                    <p className="text-[10px] text-emerald-600 font-medium flex items-center gap-1">
                      <CheckCircle className="w-3 h-3" />
                      รายการตรวจ {cp.checklist_items.length} ข้อ
                    </p>
                  </div>

                  <div className="shrink-0 p-2 bg-slate-900 rounded-xl flex flex-col items-center">
                    <QRMatrix value={cp.qr_data} size={110} />
                    <span className="text-[9px] font-mono text-white/80 mt-1">SCAN QR CODE</span>
                  </div>
                </div>

                {/* Footer preview */}
                <div className="pt-3 border-t border-slate-100 flex items-center justify-between text-[11px]">
                  <span className="text-slate-400">โครงการ นราวดี อิลิแกนซ์</span>
                  <button
                    onClick={() => setSelectedCheckpoint(cp)}
                    className="text-emerald-700 hover:text-emerald-800 font-semibold flex items-center gap-1"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    ดูการ์ดขยายใหญ่
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-white border-t border-slate-200 flex items-center justify-between text-xs">
          <span className="text-slate-500">จำนวนจุดตรวจทั้งหมด {checkpoints.length} จุด</span>
          <button
            onClick={onClose}
            className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white font-medium rounded-xl transition-colors"
          >
            ปิดหน้าต่าง
          </button>
        </div>
      </div>

      {/* Single Card Big Preview Modal */}
      {selectedCheckpoint && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/80">
          <div className="bg-white rounded-3xl p-8 max-w-sm w-full text-center space-y-4 relative border-4 border-emerald-600">
            <button
              onClick={() => setSelectedCheckpoint(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-700 text-xl font-bold"
            >
              ✕
            </button>

            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-bold rounded-full">
              <Building className="w-3.5 h-3.5" />
              นราวดี อิลิแกนซ์ • จุดตรวจ รปภ.
            </div>

            <h3 className="text-lg font-bold text-slate-900">{selectedCheckpoint.name}</h3>
            <p className="text-xs text-slate-500 font-mono font-bold bg-slate-100 py-1 px-3 rounded-lg">
              รหัสจุด: {selectedCheckpoint.code} ({selectedCheckpoint.zone})
            </p>

            <div className="p-4 bg-slate-900 rounded-2xl inline-block my-2 shadow-lg">
              <QRMatrix value={selectedCheckpoint.qr_data} size={180} />
            </div>

            <div className="bg-slate-50 p-3 rounded-xl text-left text-xs space-y-1 text-slate-600">
              <p className="font-semibold text-slate-800">รายการตรวจสอบประจำจุด:</p>
              {selectedCheckpoint.checklist_items.map((item, idx) => (
                <div key={idx} className="flex items-start gap-1.5 text-[11px]">
                  <span className="text-emerald-600 font-bold">•</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>

            <p className="text-[10px] text-slate-400">
              SECURITY GUARD TOUR PATROL SYSTEM • NARAWADEE ELEGANCE
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
